import { useEffect } from "react";
import { createRootRoute, Outlet } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/components/auth-provider";
import { QueryProvider } from "@/components/query-provider";
import { ThemeProvider } from "@/components/theme-provider";
import { LocaleProvider } from "@/i18n/locale-provider";
import itMessages from "../../messages/it.json";
import enMessages from "../../messages/en.json";

function RootComponent() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <AuthProvider>
        <LocaleProvider>
          <QueryProvider>
            <Outlet />
          </QueryProvider>
          <Toaster />
        </LocaleProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function RootErrorComponent({ error }: { error: Error }) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const copy =
    document.documentElement.lang === "en"
      ? enMessages.ErrorBoundary
      : itMessages.ErrorBoundary;

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-lg font-semibold">{copy.title}</h1>
      <p className="text-muted-foreground max-w-md text-sm">
        {copy.description}
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm"
      >
        {copy.reload}
      </button>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: RootErrorComponent,
});
